"use client";

/**
 * Rankbist's checkout window: our order summary on the left (dark band, like the dashboard), Paddle's payment
 * form embedded on the right (Paddle "inline" checkout). Replaces Paddle's full-screen overlay. Card details still
 * go only to Paddle: the form on the right is Paddle's own frame.
 *
 * The amounts come from Paddle's checkout events, so tax/VAT for the buyer's country is exactly what Paddle charges.
 * Mounted once in the root layout; opened with lib/checkoutModal.openCheckoutModal.
 */
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Check, ChevronDown, Loader2, Lock, ShieldCheck, X } from "lucide-react";
import { Portal } from "@/components/app/Portal";
import { afterPaid, closeCheckoutModal, onCheckoutModal, type CheckoutRequest } from "@/lib/checkoutModal";
import { openLayer } from "@/lib/layers";
import { loadPaddle, onPaddleEvent, type PaddleEvent } from "@/lib/paddle";
import { lockScroll } from "@/lib/scrollLock";

const FRAME = "rb-paddle-frame";

type Summary = {
  item: string;
  detail: string | null;
  currency: string;
  subtotal: number | null;
  discount: number | null;
  tax: number | null;
  total: number | null;
  recurring: number | null;
  every: string | null;
};

function money(n: number | null | undefined, currency: string) {
  if (n == null || !Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD" }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}

/** What renewals cost, in one line. With the launch discount Paddle reports the NEXT renewal (still discounted on a
 *  monthly plan), so the line says it is discounted for a while and then goes back to the normal price. */
function thenLine(s: Summary): string | null {
  if (s.recurring == null || !s.every) return null;
  const discountedRenewal = !!s.discount && s.total != null && s.recurring <= s.total + 0.01;
  return discountedRenewal
    ? `Then ${money(s.recurring, s.currency)} ${s.every} while the launch discount lasts, then the normal price, until you cancel.`
    : `Then ${money(s.recurring, s.currency)} ${s.every}, until you cancel.`;
}

function fromEvent(e: PaddleEvent, fallback: string): Summary | null {
  const d = e.data;
  if (!d) return null;
  const item = d.items?.[0];
  const cycle = item?.billing_cycle;
  const every = cycle?.interval ? (cycle.frequency && cycle.frequency > 1 ? `every ${cycle.frequency} ${cycle.interval}s` : `per ${cycle.interval}`) : null;
  return {
    item: item?.product?.name || fallback,
    detail: item?.price_name || (cycle?.interval ? `Billed ${cycle.interval === "year" ? "yearly" : cycle.interval === "month" ? "monthly" : every}` : "One-off payment"),
    currency: d.currency_code || "USD",
    subtotal: d.totals?.subtotal ?? null,
    discount: d.totals?.discount ? d.totals.discount : null,
    tax: d.totals?.tax ?? null,
    total: d.totals?.total ?? null,
    recurring: d.recurring_totals?.total ?? null,
    every,
  };
}

const STEPS = ["Creating your order", "Working out tax for your country", "Loading your payment options", "Securing the connection to Paddle"];

/** Shown until Paddle's form is ready: what is happening, step by step, instead of grey boxes. */
function Preparing({ kind }: { kind: CheckoutRequest["kind"] }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setStep((n) => Math.min(n + 1, STEPS.length - 1)), 850);
    return () => window.clearInterval(t);
  }, []);
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white px-4 text-center" role="status" aria-live="polite">
      <div className="relative flex h-16 w-16 items-center justify-center">
        <span className="absolute inset-0 animate-spin rounded-full border-[3px] border-[#ebe7ff] border-t-[#6a52ff]" style={{ animationDuration: "0.9s" }} />
        <Lock size={20} className="text-[#6a52ff]" />
      </div>
      <h3 className="mt-5 text-[17px] font-bold text-[#17142b]">Setting up your secure checkout</h3>
      <p className="mt-1 max-w-[340px] text-[13.5px] text-[#5c5878]">
        {kind === "plan" ? "Your plan starts the moment the payment goes through." : "Your credits are added the moment the payment goes through."}
      </p>
      <ul className="mt-6 w-full max-w-[320px] space-y-2.5 text-left">
        {STEPS.map((label, i) => (
          <li key={label} className={`flex items-center gap-3 text-[13.5px] transition-opacity duration-300 ${i <= step ? "opacity-100" : "opacity-40"}`}>
            <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${i < step ? "bg-[#6a52ff] text-white" : "bg-[#ebe7ff] text-[#6a52ff]"}`}>
              {i < step ? <Check size={14} strokeWidth={3} /> : i === step ? <Loader2 size={14} className="animate-spin" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
            </span>
            <span className={i <= step ? "text-[#17142b]" : "text-[#8a86a6]"}>{label}</span>
          </li>
        ))}
      </ul>
      <p className="mt-7 flex items-center gap-1.5 text-[12px] text-[#8a86a6]"><ShieldCheck size={13} /> Payments are processed by Paddle, our reseller.</p>
    </div>
  );
}

/** After Paddle says the payment is complete, while the next page loads. */
function Paid({ kind }: { kind: CheckoutRequest["kind"] }) {
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white px-4 text-center" role="status" aria-live="polite">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#e3f7ec] text-[#178a4c]"><Check size={30} strokeWidth={3} /></span>
      <h3 className="mt-5 text-[18px] font-bold text-[#17142b]">Payment successful</h3>
      <p className="mt-1 flex items-center gap-2 text-[13.5px] text-[#5c5878]">
        <Loader2 size={14} className="animate-spin" /> {kind === "plan" ? "Switching on your plan…" : "Adding your credits…"}
      </p>
    </div>
  );
}

export function CheckoutModal() {
  const [req, setReq] = useState<CheckoutRequest | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const reqRef = useRef<CheckoutRequest | null>(null);

  useEffect(() => onCheckoutModal((r) => {
    reqRef.current = r;
    setReq(r);
    setSummary(null);
    setLoaded(false);
    setError(null);
    setPaid(false);
    setDetails(false);
  }), []);

  const pushed = useRef(false); // a history entry was added for the open checkout
  const [details, setDetails] = useState(false); // phone: order breakdown shown

  function shut() {
    try {
      window.Paddle?.Checkout.close?.();
    } catch {
      /* already gone */
    }
    pushed.current = false;
    closeCheckoutModal();
  }

  /** The X, Back, Escape or the backdrop. With a history entry, going back closes it (one path for every way out). */
  function close() {
    if (pushed.current) window.history.back();
    else shut();
  }

  // Like an app screen: the phone's / browser's Back button closes checkout and leaves the person on the page
  // they came from, instead of leaving that page.
  useEffect(() => {
    if (!req) return;
    try {
      window.history.pushState({ ...(window.history.state ?? {}), rbCheckout: req.transactionId }, "");
      pushed.current = true;
    } catch {
      pushed.current = false;
    }
    const onPop = () => {
      if (!pushed.current) return;
      pushed.current = false;
      shut();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per checkout
  }, [req]);

  // Paddle's events: totals for the summary, and "completed" → where the buyer goes next.
  useEffect(() => onPaddleEvent((e) => {
    const r = reqRef.current;
    if (!r) return;
    if (e.name === "checkout.loaded" || e.name === "checkout.updated" || e.name === "checkout.items.updated" || e.name === "checkout.customer.updated") {
      setLoaded(true);
      const s = fromEvent(e, r.title);
      if (s) setSummary(s);
    }
    if (e.name === "checkout.error") setError("The payment form had a problem. Close this window and try again.");
    if (e.name === "checkout.completed") {
      const done = e.data?.transaction_id;
      if (done && done !== r.transactionId) return;
      setPaid(true);
      pushed.current = false; // leaving for the next page: our history entry stays harmlessly behind
      window.location.replace(afterPaid(r));
    }
  }), []);

  // Open Paddle's form inside our frame once the window is on screen.
  useEffect(() => {
    if (!req) return;
    let alive = true;
    const t = window.setTimeout(() => {
      loadPaddle().then((P) => {
        if (!alive) return;
        if (!P) return setError("Checkout couldn't load. Check your connection and try again.");
        P.Checkout.open({
          transactionId: req.transactionId,
          settings: {
            displayMode: "inline",
            variant: "one-page",
            theme: "light",
            frameTarget: FRAME,
            frameInitialHeight: 460,
            frameStyle: "width: 100%; min-width: 286px; background-color: transparent; border: none;",
            allowLogout: false,
            ...(req.allowDiscountCodes === false ? { showAddDiscounts: false } : {}),
          },
        });
        // If Paddle's "loaded" event is late, never keep the form hidden behind the placeholder.
        window.setTimeout(() => alive && setLoaded(true), 6000);
      }).catch(() => alive && setError("Checkout couldn't load. Check your connection and try again."));
    }, 40);
    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, [req]);

  useEffect(() => {
    if (!req) return;
    const closeLayer = openLayer({ panel: () => panelRef.current, onEscape: close });
    const unlock = lockScroll();
    return () => {
      unlock();
      closeLayer();
    };
  }, [req]);

  if (!req) return null;
  const s = summary;
  const currency = s?.currency ?? "USD";

  return (
    <Portal>
      <div className="fixed inset-0 z-[1100] flex items-end justify-center overscroll-none sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-label="Checkout">
        {/* Light dim, slight blur: the page the person came from (their dashboard, the pricing page) stays visible behind */}
        <div className="absolute inset-0 bg-[#0f0a2e]/40 backdrop-blur-[2px]" aria-hidden onClick={close} />
        <div ref={panelRef}
          className="relative flex h-[100dvh] w-full max-w-[980px] flex-col overflow-hidden bg-white sm:h-[min(90dvh,820px)] sm:rounded-3xl sm:shadow-[0_40px_100px_-20px_rgba(15,10,46,0.6)] sm:ring-1 sm:ring-black/5 md:flex-row">
          {/* Phones: the whole screen. Larger screens: a window. A FIXED height, from the screen only: loading, Paddle's form growing or a payment method's extra fields
              never resize the window; the payment side scrolls inside it instead. */}
          {/* Summary */}
          <aside className="relative hidden flex-shrink-0 overflow-hidden bg-gradient-to-br from-[#221a5c] via-[#1a1446] to-[#140f33] px-5 pb-5 pt-[max(1.25rem,env(safe-area-inset-top))] text-white sm:block sm:px-7 sm:py-7 md:w-[40%] md:py-9">
            <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-[#6a52ff] opacity-40 blur-[80px]" aria-hidden />
            <div className="relative flex items-center justify-between gap-3">
              <span className="text-[13px] font-semibold uppercase tracking-[0.12em] text-white/55">Rankbist checkout</span>
              <button type="button" onClick={close} aria-label="Close checkout"
                className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white md:hidden">
                <X size={18} />
              </button>
            </div>
            <div className="relative mt-4 md:mt-8">
              <div className="text-[13px] text-white/60">{s?.detail ?? (req.kind === "plan" ? "Subscription" : "One-off payment")}</div>
              <div className="mt-1 break-words text-[22px] font-bold leading-tight sm:text-[26px]">{s?.item ?? req.title}</div>
              <div className="mt-4 flex items-baseline gap-2 md:mt-6">
                <span className="text-[30px] font-bold leading-none sm:text-[38px]">{loaded ? money(s?.total, currency) : <span className="inline-block h-9 w-32 animate-pulse rounded-lg bg-white/10 align-middle" />}</span>
                <span className="text-[13px] text-white/60">due today</span>
              </div>
              {loaded && s && thenLine(s) && <p className="mt-2 text-[13px] text-white/70">{thenLine(s)}</p>}
            </div>
            {/* Breakdown: hidden on small phones to keep the card form near the top */}
            <dl className="relative mt-7 hidden space-y-2.5 border-t border-white/10 pt-5 text-[13.5px] sm:block">
              <div className="flex justify-between gap-4"><dt className="text-white/65">Subtotal</dt><dd>{loaded ? money(s?.subtotal, currency) : "…"}</dd></div>
              {s?.discount ? <div className="flex justify-between gap-4"><dt className="text-white/65">Discount</dt><dd>−{money(s.discount, currency)}</dd></div> : null}
              <div className="flex justify-between gap-4"><dt className="text-white/65">Tax / VAT</dt><dd>{loaded ? money(s?.tax, currency) : "…"}</dd></div>
              <div className="flex justify-between gap-4 border-t border-white/10 pt-2.5 font-semibold"><dt>Total</dt><dd>{loaded ? money(s?.total, currency) : "…"}</dd></div>
            </dl>
            <ul className="relative mt-7 hidden space-y-2 text-[12.5px] text-white/60 md:block">
              <li className="flex items-center gap-2"><Lock size={14} /> Card details go straight to Paddle, never to Rankbist.</li>
              <li className="flex items-center gap-2"><ShieldCheck size={14} /> {req.kind === "plan" ? "Cancel any time from Plan & billing." : "Credits never expire with the month."}</li>
            </ul>
          </aside>

          {/* Paddle's payment form */}
          <section className="relative flex min-h-0 flex-1 flex-col">
            {/* Phone: an app screen's top bar */}
            <header className="flex flex-shrink-0 items-center gap-2 border-b border-[#ece9f6] bg-white px-2 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))] sm:hidden">
              <button type="button" onClick={close} className="flex h-11 items-center gap-1 rounded-full pl-2 pr-3 text-[15px] font-semibold text-[#6a52ff] active:bg-[#f3f1fb]" aria-label="Back">
                <ArrowLeft size={20} /> Back
              </button>
              <h2 className="flex-1 text-center text-[16px] font-bold text-[#17142b]">Checkout</h2>
              <span className="flex h-11 items-center gap-1 pr-3 text-[12px] font-semibold text-[#178a4c]"><Lock size={14} /> Secure</span>
            </header>
            <div className="hidden flex-shrink-0 items-center justify-between gap-3 px-7 pt-6 md:flex">
              <h2 className="text-[17px] font-bold text-[#17142b]">Payment details</h2>
              <button type="button" onClick={close} aria-label="Close checkout"
                className="flex h-8 w-8 items-center justify-center rounded-full text-[#8a86a6] transition-colors hover:bg-[#f3f1fb] hover:text-[#17142b]">
                <X size={18} />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-3 sm:px-7">
              {/* Phone: the order as a card that scrolls with the form */}
              <div className="mb-4 overflow-hidden rounded-2xl bg-gradient-to-br from-[#2c1c86] via-[#1a1446] to-[#140f33] text-white sm:hidden">
                <div className="px-4 pb-3 pt-4">
                  <div className="text-[12.5px] text-white/60">{s?.detail ?? (req.kind === "plan" ? "Subscription" : "One-off payment")}</div>
                  <div className="mt-0.5 flex items-end justify-between gap-3">
                    <div className="min-w-0 break-words text-[19px] font-bold leading-tight">{s?.item ?? req.title}</div>
                    <div className="flex-shrink-0 text-right">
                      <div className="text-[22px] font-bold leading-none">{loaded ? money(s?.total, currency) : <span className="inline-block h-6 w-20 animate-pulse rounded bg-white/10" />}</div>
                      <div className="mt-1 text-[11.5px] text-white/60">due today</div>
                    </div>
                  </div>
                  {loaded && s && thenLine(s) && <p className="mt-2 text-[12.5px] text-white/70">{thenLine(s)}</p>}
                </div>
                <button type="button" onClick={() => setDetails((v) => !v)} aria-expanded={details}
                  className="flex w-full items-center justify-between border-t border-white/10 px-4 py-2.5 text-[13px] text-white/75 active:bg-white/5">
                  Order details <ChevronDown size={16} className={`transition-transform ${details ? "rotate-180" : ""}`} />
                </button>
                {details && (
                  <dl className="space-y-2 px-4 pb-4 text-[13px]">
                    <div className="flex justify-between"><dt className="text-white/65">Subtotal</dt><dd>{loaded ? money(s?.subtotal, currency) : "…"}</dd></div>
                    {s?.discount ? <div className="flex justify-between"><dt className="text-white/65">Discount</dt><dd>−{money(s.discount, currency)}</dd></div> : null}
                    <div className="flex justify-between"><dt className="text-white/65">Tax / VAT</dt><dd>{loaded ? money(s?.tax, currency) : "…"}</dd></div>
                    <div className="flex justify-between border-t border-white/10 pt-2 font-semibold"><dt>Total</dt><dd>{loaded ? money(s?.total, currency) : "…"}</dd></div>
                  </dl>
                )}
              </div>
              {error && <div className="mb-3 rounded-xl border border-[#f5c2cb] bg-[#fde4e8] px-4 py-3 text-[13.5px] text-[#b4233c]">{error}</div>}
              <div className="relative min-h-[420px] flex-1">
                <div className={FRAME} />
                {/* Shown over Paddle's frame until it has drawn its form */}
                {!loaded && !error && <Preparing kind={req.kind} />}
                {paid && <Paid kind={req.kind} />}
              </div>
            </div>
          </section>
        </div>
      </div>
    </Portal>
  );
}
